import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { useLulcStore } from './lulc.store';
import type {
  LulcBiomassDensityResponse,
  LulcChangeResponse,
  LulcForestCoverLayerResponse,
  LulcLayerResponse,
  LulcStatsResponse,
  LulcTimeseriesResponse,
} from './lulc.types';

/**
 * BE-side `LULC10M_DEFAULT_YEAR` — the year `/lulc10m/layer` returns
 * when `?year=` is omitted. Mirroring it here lets the cache key be
 * consistent regardless of whether a caller passes `year=2025`
 * explicitly or relies on the default.
 *
 * Source: `analytics/lulc10m/config.py:DEFAULT_YEAR` (also `MIN_YEAR`
 * 2020 / `MAX_YEAR` 2025). 422 `INVALID_YEAR` / `YEAR_OUT_OF_RANGE`
 * envelopes returned for out-of-range or non-int input.
 */
const LULC_DEFAULT_YEAR = 2025;

@Composable()
export class useLulcService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useLulcStore())
  public readonly lulcStore!: ReturnType<typeof useLulcStore>;

  /**
   * Tile URL for the LULC 10m product at `year`. Defaults to the
   * BE's current vintage. Cached per year in `lulcStore`; `signal`
   * only matters on the cache-miss path since the cached return is
   * synchronous. Concurrent callers each fire their own request (no
   * `InFlightCell` here), so an abort on one caller can't strand
   * another.
   */
  public async getLulc(
    year: number = LULC_DEFAULT_YEAR,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LulcLayerResponse>> {
    const cached = this.lulcStore.getLayer(year);
    if (cached) {
      return cached;
    }

    const res = await this.api.get<LulcLayerResponse>(
      `${import.meta.env.VITE_GEE_API}/lulc10m/layer?year=${year}`,
      { signal },
    );

    if (res.isSuccess && res.data) {
      this.lulcStore.setLayer(year, res);
    }
    return res;
  }

  /**
   * Tile URL for the **report-only** recolored forest-cover map at `year`
   * (natural forest → green, other land → grey, water → blue). Backed by
   * `GET /lulc10m/forest-cover/tile-template` (see contract
   * 260714_forest_cover_compliance). Not cached — it's fetched once per report.
   * Returns `{tileUrl}` per the contract; a bare string is tolerated by the
   * caller (same BE serialisation quirk as `getLulc`). No-ops gracefully (the
   * caller omits the map) until the endpoint is deployed.
   */
  public async getForestCoverLayer(
    year: number = LULC_DEFAULT_YEAR,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LulcForestCoverLayerResponse | string>> {
    return this.api.get<LulcForestCoverLayerResponse | string>(
      `${import.meta.env.VITE_GEE_API}/lulc10m/forest-cover/tile-template?year=${year}`,
      { signal },
    );
  }

  /** Per-class hectares in `coords` for the LULC product at `year`. */
  public async getLulcStats(
    coords: MultiPolygonCoords,
    year: number = LULC_DEFAULT_YEAR,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LulcStatsResponse>> {
    return this.api.post<LulcStatsResponse>(
      `${import.meta.env.VITE_GEE_API}/lulc10m/stats?year=${year}`,
      { data: coords },
      { signal },
    );
  }

  /** Per-class hectares for every configured LULC year, at one reported scale. */
  public async getLulcTimeseries(
    coords: MultiPolygonCoords,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LulcTimeseriesResponse>> {
    return this.api.post<LulcTimeseriesResponse>(
      `${import.meta.env.VITE_GEE_API}/lulc10m/timeseries`,
      { data: coords },
      { signal },
    );
  }

  /** Detailed 12-class snapshot transition matrix between two LULC years. */
  public async getLulcChange(
    coords: MultiPolygonCoords,
    from: number,
    to: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<LulcChangeResponse>> {
    return this.api.post<LulcChangeResponse>(
      `${import.meta.env.VITE_GEE_API}/lulc10m/change?from=${from}&to=${to}&scheme=detailed&axis=all`,
      { data: coords },
      { signal },
    );
  }

  // Mean GEDI L4A biomass density (Mg/ha) per LULC class for the AOI. Pairs with
  // getLulcStats to compute carbon potential (hectares × density). No year
  // parameter — backend uses the default LULC × full GEDI archive.
  public async getLulcBiomassDensity(
    coords: MultiPolygonCoords,
  ): Promise<ApiResponse<LulcBiomassDensityResponse>> {
    return this.api.post<LulcBiomassDensityResponse>(
      `${import.meta.env.VITE_GEE_API}/lulc10m/biomass-density`,
      { data: coords },
    );
  }
}
